import React, { useState } from "react";
import NavigationLink from "../../NavigationLink";
import ToolTip from "../../ToolTip";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { ChevronDownIcon } from "@heroicons/react/solid";
import {
  hasMealPlaner,
  hasShoppingList,
  selectUser,
} from "../../../redux/user/userSlice";
import Button from "../../Button";
import GoProModal from "../../Modals/GoProModal";
import { useMediaQuery } from "react-responsive";

const MealPlanRow = ({ item, additionalParam, index, onDelete }) => {
  const currentUser = useSelector(selectUser);
  const { _id, title, recipes, featured_date, primary_image } = item;

  const convertDate = new Date(featured_date);
  const date = new Intl.DateTimeFormat("en-US", {}).format(convertDate);

  const hasShoppingListPermission = useSelector(hasShoppingList);
  const hasMealPlanerPermission = useSelector(hasMealPlaner);

  const [open, setOpen] = useState(false);
  const largeScreen = useMediaQuery({ query: "(max-width: 1024px)" });
  const smallScreen = useMediaQuery({ query: "(max-width: 640px)" });

  // Match other buttons - collapse label on medium screens
  const deleteLabel = largeScreen ? "Delete" : "Delete Meal Plan";

  const [openMenuIndex, setOpenMenuIndex] = useState(-1);
  const handleOpenClick = (rowIndex) => {
    if (openMenuIndex === rowIndex) return setOpenMenuIndex(-1);
    setOpenMenuIndex(rowIndex);
  };

  const handleDeleteClick = () => {
    if (!onDelete) return;

    const ok = window.confirm(`Delete "${title}"? This can't be undone.`);
    if (!ok) return;

    onDelete(_id);
  };

  return (
    <>
      <tr>
        <td className="hidden sm:table-cell align-top pt-4 mr-4">
          {title}
          {primary_image && (
            <img
              src={primary_image}
              alt=""
              className="w-full md:w-11/12 object-cover my-3 h-28 border rounded-xl"
            />
          )}
        </td>

        {/* Mobile row layout */}
        <td className="sm:hidden py-4 flex items-center justify-between">
          <div className="flex items-center gap-x-4">
            {primary_image && (
              <img
                src={primary_image}
                alt=""
                className="w-16 h-16 object-cover my-3 border rounded-xl"
              />
            )}

            <div>
              <h1
                onClick={() => handleOpenClick(index)}
                className="font-inter font-medium text-sm text-wwlGray900 cursor-pointer"
              >
                {title}
              </h1>

              <p
                onClick={() => handleOpenClick(index)}
                className="cursor-pointer text-wwlGray500 text-xs"
              >
                {recipes?.length} recipes{" "}
                <ChevronDownIcon className="ml-2 w-3 h-3 text-wwlGray500 inline" />
              </p>

              {openMenuIndex === index && (
                <ul className="list-disc mt-4">
                  {recipes
                    ?.filter((recipe) => Object.keys(recipe).length !== 0)
                    .map((recipe) => (
                      <li
                        className="text-xs text-wwlGray500 underline cursor-pointer"
                        key={recipe.title}
                      >
                        <Link to={`/recipe-detail/${recipe.id}`}>
                          {recipe.title}
                        </Link>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          </div>

          {/* Mobile action buttons */}
          <div>
            <div className="mb-2">
              {hasMealPlanerPermission ? (
                <NavigationLink
                  url={`/meal-planning/meal-planner?mp=${_id}&t=${title}${additionalParam}`}
                  linkText="Open"
                  linkSubText="in Meal Planner"
                  extraClasses="bg-wwlOrange text-wwlWhite border-transparent hover:border-wwlOrange hover:bg-wwlWhite hover:text-wwlOrange text-center w-full block lg:w-48 py-1 text-xs"
                />
              ) : (
                <Button
                  btnText="Open"
                  extraClasses="bg-transparent text-wwlOrange border border-wwlOrange hover:border-transparent hover:bg-wwlOrange hover:text-wwlWhite text-center w-full block lg:w-48 py-1 rounded-lg text-xs px-3.5"
                  onClick={() => setOpen(true)}
                />
              )}
            </div>

            <div className="mb-2">
              {hasShoppingListPermission ? (
                <NavigationLink
                  url={`/shopping-list?mp=${_id}&t=${title} (${currentUser.name} Version)`}
                  linkText={smallScreen ? "Shop" : "View"}
                  linkSubText="Shopping List"
                  extraClasses="bg-wwlGreen text-wwlDarkBlue border-transparent hover:border-wwlDarkBlue hover:bg-wwlWhite hover:text-wwlGreen text-center w-full block lg:w-48 py-1 text-xs"
                />
              ) : (
                <Button
                  btnText={smallScreen ? "Shop" : "View"}
                  extraClasses="bg-wwlGreen text-wwlDarkBlue border-transparent hover:bg-wwlWhite hover:text-wwlGreen text-center w-full block border lg:w-48 py-1 text-xs px-1 rounded-lg hover:border-wwlDarkBlue sm:px-[17px] lg:px-8"
                  onClick={() => setOpen(true)}
                />
              )}
            </div>

            {/* Mobile Delete */}
            {onDelete && (
              <div className="mt-1 w-full">
                {smallScreen ? (
                  <button
                    type="button"
                    onClick={handleDeleteClick}
                    className="bg-transparent text-wwlOrange border border-wwlOrange
                               hover:border-transparent hover:bg-wwlOrange hover:text-wwlWhite
                               text-center w-full block lg:w-48 py-1 text-xs
                               rounded-lg transition-colors duration-300"
                  >
                    Delete
                  </button>
                ) : (
                  <ToolTip content="Delete Meal Plan" direction="bottom">
                    <span className="block w-full">
                      <button
                        type="button"
                        onClick={handleDeleteClick}
                        className="bg-transparent text-wwlOrange border border-wwlOrange
                                   hover:border-transparent hover:bg-wwlOrange hover:text-wwlWhite
                                   text-center w-full block lg:w-48 py-1 text-xs
                                   rounded-lg transition-colors duration-300"
                      >
                        Delete
                      </button>
                    </span>
                  </ToolTip>
                )}
              </div>
            )}
          </div>
        </td>

        <td className="hidden sm:table-cell align-top pt-4 text-center">
          {date}
        </td>

        <td className="hidden sm:table-cell align-top pt-4 pr-2">
          <ul className="list-disc">
            {recipes
              ?.filter((recipe) => Object.keys(recipe).length !== 0)
              .map((recipe) => (
                <li className="underline" key={recipe.title}>
                  <Link to={`/recipe-detail/${recipe.id}`}>{recipe.title}</Link>
                </li>
              ))}
          </ul>
        </td>

        {/* Desktop action column */}
        <td className="hidden sm:table-cell py-8">
          <ToolTip content="Meal Planner" direction="top" extraClasses="mb-2">
            {hasMealPlanerPermission ? (
              <NavigationLink
                url={`/meal-planning/meal-planner?mp=${_id}&t=${title}${additionalParam}`}
                linkText="Open"
                linkSubText="in Meal Planner"
                // ✅ FIX: make medium screens same width as delete
                extraClasses="bg-wwlOrange text-wwlWhite border-transparent hover:border-wwlOrange hover:bg-wwlWhite hover:text-wwlOrange text-center w-full sm:w-48"
              />
            ) : (
              <Button
                btnText={`Open ${largeScreen ? "" : "in Meal Planner"} `}
                extraClasses="bg-transparent text-wwlOrange hover:border-transparent hover:bg-wwlOrange hover:text-wwlWhite text-center w-full sm:w-48 border border-wwlOrange rounded-lg sm:px-[17px] lg:px-8"
                onClick={() => setOpen(true)}
              />
            )}
          </ToolTip>

          <ToolTip content="Shopping List" direction="bottom" extraClasses="mb-2">
            {hasShoppingListPermission ? (
              <NavigationLink
                url={`/shopping-list?mp=${_id}&t=${title} (${currentUser.name} Version)`}
                linkText="View"
                linkSubText="Shopping List"
                // ✅ FIX: make medium screens same width as delete
                extraClasses="bg-wwlGreen text-wwlDarkBlue border-transparent hover:border-wwlDarkBlue hover:bg-wwlWhite hover:text-wwlGreen text-center w-full sm:w-48"
              />
            ) : (
              <Button
                btnText={`View ${largeScreen ? "" : "Shopping List"}`}
                extraClasses="bg-wwlGreen text-wwlDarkBlue border-transparent hover:border-wwlDarkBlue hover:bg-wwlWhite hover:text-wwlGreen rounded-lg text-sm border text-center w-full sm:w-48 sm:px-[17px] lg:px-8"
                onClick={() => setOpen(true)}
              />
            )}
          </ToolTip>

          {onDelete && (
            <ToolTip content="Delete Meal Plan" direction="bottom">
              <button
                type="button"
                onClick={handleDeleteClick}
                className="bg-transparent text-wwlOrange border border-wwlOrange
                           hover:border-transparent hover:bg-wwlOrange hover:text-wwlWhite
                           text-center w-full sm:w-48 rounded-lg
                           sm:px-[17px] lg:px-8 py-1.5 text-sm transition-colors duration-300"
              >
                {deleteLabel}
              </button>
            </ToolTip>
          )}
        </td>
      </tr>

      <GoProModal open={open} setOpen={setOpen} />
    </>
  );
};

export default MealPlanRow;